import { SHOT_LANDING_LABELS } from '@golf/common';
import type { ShotLanding } from '@golf/common';
import { Check, GraduationCap, History, Lightbulb, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { ClubIcon } from '../../components/ChoixDuClub.tsx';

/* LA PASTILLE DU DERNIER PASSAGE.
 *
 * Un disque de la couleur de l'endroit atteint : vert clair pour le green,
 * sable pour le bunker, vert sombre pour le rough, brique pour le hors-limites.
 * Ce sont les MÊMES teintes que celles qu'on touche en saisissant un coup, et
 * que porte la carte des zones dans les statistiques — le joueur n'a donc rien
 * de neuf à apprendre.
 *
 * Une couleur à la place d'une phrase : « Votre coup 1 sur ce trou la dernière
 * fois — Green » pesait deux lignes sous chaque tuile, sur un écran qu'on lit
 * debout entre deux coups. Le disque dit la même chose sans rien à lire.
 *
 * La couleur ne porte pas SEULE pour autant : l'horloge à l'intérieur dit
 * « déjà joué » quelle que soit la teinte, et le nom de la zone part dans
 * l'étiquette d'accessibilité — c'est la règle que suit déjà la carte des
 * zones : la couleur confirme, elle ne dit rien toute seule. */
function PastilleZone({ landing }: { landing: string | null }) {
    const connue = landing != null && (SHOT_LANDING_LABELS as Record<string, string>)[landing] != null;
    const mot = connue ? SHOT_LANDING_LABELS[landing as ShotLanding] : null;
    return (
        <span
            aria-label={mot ? `Déjà joué ici la dernière fois — ${mot}` : 'Déjà joué ici la dernière fois'}
            title={mot ?? undefined}
            className="flex size-11 shrink-0 items-center justify-center rounded-full shadow-sm ring-2 ring-white/40"
            style={connue
                ? {
                    background: `var(--zone-${landing})`,
                    color: `var(--zone-${landing}-encre)`,
                }
                : { background: 'var(--brass-ink)', color: 'var(--color-base-100)' }}>
            <History size={20} aria-hidden="true" />
        </span>
    );
}

export interface ClubAChoisir {
    id: string;
    label: string;
    /** La distance annoncée, telle qu'écrite sur la fiche du club. */
    distance: string | null;
    /** Le taux de centrage mesuré au practice, en pourcentage entier. */
    taux: number | null;
}

/* Le choix du club, PLEINE PAGE, entre deux coups.
 *
 * ── Pourquoi un écran, et non un tiroir ─────────────────────────────────────
 *
 * Le sac s'ouvrait sous la carte, en bas de l'écran de jeu, et se refermait au
 * premier tap. Rien n'obligeait à le regarder : on pouvait enchaîner trois
 * coups avec le club de la dernière fois sans jamais se poser la question. Or
 * c'est LA question du golf — le geste vient après.
 *
 * Un écran plein s'interpose. Il ne se contourne pas d'un pouce distrait, il se
 * traverse, et il ne coûte qu'un tap puisqu'il faut de toute façon désigner
 * quelque chose. C'est le même raisonnement que le bilan du coup : ce qui
 * mérite une décision mérite un écran.
 *
 * ── Trois états, trois couleurs ─────────────────────────────────────────────
 *
 * Le LAITON pour la suggestion, et il respire — c'est un conseil, il attire
 * l'œil sans commander. Le VERT pour le club déjà en main : c'est un fait, pas
 * une proposition, et le distinguer évite de reprendre pour la troisième fois
 * un club qu'on tenait déjà. Le sable pour le reste du sac.
 *
 * Les deux ne se confondent jamais parce qu'ils ne disent pas la même chose :
 * l'un est ce qu'on vous propose, l'autre ce que vous avez.
 */
export function ChoixClubEcran({
    trou, nbTrous, distance, clubs, suggestionId, raison, courantId,
    memoireId, memoireLanding, onOutil, outilOuvert,
    onChoisir, onFermer,
}: {
    trou: number;
    nbTrous: number;
    /** La distance à couvrir, déjà mise en forme. */
    distance: string | null;
    clubs: ClubAChoisir[];
    /** Le club conseillé pour ce coup. `null` sans distance connue. */
    suggestionId: string | null;
    /** Pourquoi celui-là. Affiché sous la tuile dorée. */
    raison: ReactNode;
    courantId: string | null;
    /** Le club joué à ce même rang la dernière fois sur ce trou.
     *
     *  Un fait, pas un avis — et c'est justement quand il contredit la
     *  suggestion qu'il vaut d'être montré. */
    memoireId?: string | null;
    /** Où la balle était allée, ce jour-là : c'est la couleur de la pastille. */
    memoireLanding?: string | null;
    /** L'avis du prof : on lui donne une distance, il désigne un club.
     *
     *  La saisie elle-même s'ouvre en MODAL, par-dessus cet écran : la distance
     *  vient d'un télémètre, elle se tape au chiffre près, et un champ déplié
     *  au milieu d'une liste que le clavier recouvre à moitié se manque une
     *  fois sur deux. */
    onOutil?: () => void;
    outilOuvert?: boolean;
    onChoisir: (id: string) => void;
    onFermer: () => void;
}) {
    const courant = clubs.find((c) => c.id === courantId) ?? null;

    return (
        <div className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-[var(--fairway-deep)]">
            <header className="shrink-0 px-4 pb-2 pt-5 text-center safe-top">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/60">
                    Trou {trou}/{nbTrous}
                    {distance && <span>{' · '}{distance} à couvrir</span>}
                </p>
                <h1 className="font-display mt-0.5 text-3xl font-bold leading-tight text-white">
                    Quel club ?
                </h1>
            </header>

            <div className="min-h-0 flex-1 space-y-2 px-3 py-2">
                {/* L'OUTIL, EN PREMIÈRE TUILE, avant le driver.
                 *
                 * Au format d'un club, parce que c'est un choix de la même
                 * famille : « avec quoi » commence souvent par « combien ». En
                 * jeton d'en-tête il se lisait comme un réglage de l'écran ; en
                 * tête de liste, c'est la première chose qu'on touche, et la
                 * cible du pouce est celle d'une tuile.
                 *
                 * L'icône seule : le titre dit déjà la distance à couvrir, et
                 * une tuile qui répète l'en-tête est du bruit. */}
                {onOutil && (
                    <div>
                        <button type="button" onClick={onOutil}
                            aria-label="Donner la distance à couvrir"
                            aria-expanded={outilOuvert ?? false}
                            /* L'icône à GAUCHE, exactement où se trouve celle des
                               clubs : c'est ce qui fait qu'on lit « une tuile de
                               plus » et non un bandeau étranger posé au-dessus
                               de la liste. */
                            className="flex w-full items-center gap-3 rounded-box p-3 shadow-md transition-transform active:scale-[0.98]"
                            style={{ backgroundColor: 'var(--brass-ink)', color: 'var(--color-base-100)' }}>
                            <span className="flex size-11 shrink-0 items-center justify-center rounded-field"
                                style={{ backgroundColor: 'rgb(255 255 255 / 0.18)' }}>
                                <GraduationCap size={24} aria-hidden="true" />
                            </span>
                        </button>
                    </div>
                )}

                {clubs.map((c) => {
                    const propose = c.id === suggestionId;
                    const enMain = c.id === courantId && !propose;
                    const souvenir = c.id === memoireId;
                    return (
                        <div key={c.id}>
                            <button type="button" onClick={() => onChoisir(c.id)}
                                className={`flex w-full items-center gap-3 rounded-box p-3 text-left
                                            shadow-md transition-transform active:scale-[0.98]
                                            ${propose ? 'battement-or' : ''}`}
                                style={propose
                                    ? { backgroundColor: 'var(--color-accent)', color: 'var(--color-accent-content)' }
                                    : enMain
                                        ? { backgroundColor: 'var(--color-primary)', color: 'var(--color-primary-content)' }
                                        : { backgroundColor: 'var(--tuile)', color: 'var(--tuile-encre)' }}>
                                <span className="flex size-11 shrink-0 items-center justify-center rounded-field"
                                    style={{
                                        backgroundColor: propose || enMain
                                            ? 'rgb(255 255 255 / 0.22)' : 'var(--fairway-deep)',
                                        color: propose || enMain ? 'currentColor' : 'var(--color-accent)',
                                    }}>
                                    <ClubIcon size={22} />
                                </span>

                                <span className="min-w-0 flex-1">
                                    {/* `leading-[1.15]` : la condensée descend sous
                                        la ligne de base, et `leading-none` raserait
                                        le jambage du « g » de « wedge ». */}
                                    <span className="font-score block truncate text-2xl leading-[1.15]">
                                        {c.label}
                                    </span>
                                    <span className="mt-0.5 block truncate text-xs opacity-80">
                                        {[c.distance, c.taux != null ? `fiabilité ${c.taux} %` : null]
                                            .filter(Boolean).join(' · ') || '—'}
                                    </span>
                                </span>

                                {/* Le mot, pas seulement la couleur : sous un
                                    soleil rasant, deux aplats se ressemblent, et
                                    « suggéré » ne se devine pas. */}
                                {propose && (
                                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-black/15 px-2 py-0.5 text-[11px] font-bold">
                                        <Lightbulb size={12} aria-hidden="true" /> suggéré
                                    </span>
                                )}
                                {enMain && (
                                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-black/20 px-2 py-0.5 text-[11px] font-bold">
                                        <Check size={12} aria-hidden="true" /> en main
                                    </span>
                                )}
                                {/* Le souvenir se pose À CÔTÉ des deux autres, et
                                    jamais à leur place : le même club peut être
                                    à la fois suggéré et déjà joué ici. */}
                                {souvenir && <PastilleZone landing={memoireLanding ?? null} />}
                            </button>

                            {/* La raison se lit SOUS la tuile qu'elle explique.
                                Ailleurs, elle devient une note de bas de page
                                qu'on rapporte de tête au bon club. */}
                            {propose && raison && (
                                <p className="mt-1 px-2 text-center text-xs text-white/70">{raison}</p>
                            )}

                        </div>
                    );
                })}
            </div>

            {/* La sortie sans rien changer. Elle existe parce qu'on rouvre
                parfois cet écran par curiosité, ou parce qu'on l'a ouvert au
                mauvais moment — et parce qu'un écran dont on ne peut pas sortir
                sans agir est un piège, pas un rappel. */}
            <div className="shrink-0 px-3 pb-4 pt-1 safe-bottom">
                <button type="button" onClick={onFermer}
                    className="btn btn-ghost btn-block gap-2 font-semibold !text-white/80">
                    <X size={16} aria-hidden="true" />
                    {courant ? `Garder le ${courant.label}` : 'Fermer'}
                </button>
            </div>
        </div>
    );
}
